import React, { useCallback } from 'react'
import { FormInputGroup, FormTextField, useWatch, useZionFormContext } from '@fs/zion-form'
import { FlowGrid, H6, InlineButton, Paragraph, Radio, RadioGroup, Row, Separator, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import PrimaryPersonInfoOverlay from './PrimaryPersonInfoOverlay'

const PersonalDetailsForm = () => {
  const [t] = useTranslation()
  const {
    formState: { errors, isSubmitted },
    setValue,
  } = useZionFormContext()
  const notSureOverlay = useOverlay()
  const [primaryPersonValue] = useWatch({ name: ['primaryPerson'] })

  const handlePrimaryPersonChange = useCallback(
    ({ target: { value } }) => {
      setValue('primaryPerson', value === 'true', { shouldDirty: true, shouldValidate: true })
    },
    [setValue]
  )

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="nano">
      <H6>{t('essentialInformationForm.primaryPerson.title', 'Primary Person')}</H6>
      <>
        <Paragraph size="sm">
          {t('essentialInformationForm.primaryPerson.label', 'Is the document primarily about this person?')}{' '}
          <InlineButton
            onClick={notSureOverlay.open}
            linkName="primary-person-not-sure"
            data-testid="primary-person-not-sure-button"
          >
            <em>{t('essentialInformationForm.primaryPerson.notSure', 'Not sure?')}</em>
          </InlineButton>
        </Paragraph>
        <RadioGroup
          data-testid="primary-person-radio-group"
          orientation="horizontal"
          errorMessage={isSubmitted && errors?.primaryPerson?.message}
          dense
        >
          <Radio
            name="primaryPerson"
            value="true"
            checked={primaryPersonValue === true}
            label={t('essentialInformationForm.primaryPerson.yes', 'Yes')}
            onChange={handlePrimaryPersonChange}
          />
          <Radio
            name="primaryPerson"
            value="false"
            checked={primaryPersonValue === false}
            label={t('essentialInformationForm.primaryPerson.no', 'No')}
            onChange={handlePrimaryPersonChange}
          />
        </RadioGroup>

        {primaryPersonValue === false && (
          <>
            <Paragraph size="sm">
              {t(
                'essentialInformationForm.primaryPerson.whoIsItAbout',
                'Who is the document primarily about? (you can add more information about him or her later)'
              )}
            </Paragraph>
            <Separator size="xxs" />
            <FormInputGroup namesOfFieldsToValidate={['primaryPersonGivenName', 'primaryPersonSurname']} dense>
              <Row gutters="xxs">
                <FormTextField
                  name="primaryPersonGivenName"
                  label={t('essentialInformationForm.primaryPerson.givenName', 'Given Name')}
                  data-testid="primary-person-given-name-field"
                  validateAtGroupLevel
                  growX
                />
                <FormTextField
                  name="primaryPersonSurname"
                  label={t('essentialInformationForm.primaryPerson.surname', 'Surname')}
                  data-testid="primary-person-surname-field"
                  validateAtGroupLevel
                  growX
                />
              </Row>
            </FormInputGroup>
          </>
        )}

        <PrimaryPersonInfoOverlay overlay={notSureOverlay} />
      </>
    </FlowGrid>
  )
}

export default PersonalDetailsForm
